import ChangeName from '../../components/common/change-name/ChangeName';
import { confirmDialog } from 'primereact/confirmdialog';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { ProjectNameData } from '../../models/projects/ProjectNameData';
import { ProjectResponse } from '../../models/projects/ProjectResponse';
import { useAccount } from '../../contexts/account/AccountContexts';
import { changeProjectNameOrThrow } from '../../services/projects/ProjectService';
import { GET_PROJECT } from '../../api/query/project/keys';
import { MonitoringInterval, sameInterval } from '../../models/projects/MonitoringInterval';

const BTN_DISABLED = 'btn--disabled';
const BTN_SELECTOR = '.confirm-change-name-btn';

const updateSaveButtonState = (disabled: boolean) => {
  const btn = document.querySelector(BTN_SELECTOR) as HTMLElement;
  if (btn) {
    btn.classList.toggle(BTN_DISABLED, disabled);
  }
};

const useChangeName = ({
  onRefresh,
  onSaveStart,
  onSaveEnd,
  settleDelayMs = 0,
}: {
  onRefresh: () => void;
  // Optional per-row hooks so a caller (ProjectsPage) can lock just the edited row while the
  // save is in flight, instead of any page-level loader. No-op when omitted (V1/preview).
  onSaveStart?: (id: number) => void;
  onSaveEnd?: (id: number) => void;
  // Artificial hold (ms) AFTER the save+patch before `onSaveEnd`, so the row's lock/spinner stays
  // visible long enough for a reveal animation. Cosmetic — only the spinner lingers; the new label is
  // already applied. Success path only (errors clear immediately).
  settleDelayMs?: number;
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { projectResponse, setProjectResponse, refetchAccountSilently } = useAccount();

  let newProjectLabel = '';
  let newProjectCountry: string | null = null;
  let newMonitoringInterval: MonitoringInterval | undefined;
  // undefined = untouched; null = the limit was removed.
  let newPromptAllocation: number | null | undefined;

  const resetValues = () => {
    updateSaveButtonState(true);
  };

  const changeProjectNameEvent = async (id: number) => {
    const currentProject = projectResponse?.find((project) => project.id === id);
    if (!currentProject) {
      return;
    }

    // Sent only when actually changed: the field is addon-gated server-side, and an unchanged value
    // must not even reach the gate (a user without the addon still renames and relocates freely).
    const scheduleChanged =
      newMonitoringInterval !== undefined &&
      !sameInterval(newMonitoringInterval, currentProject.monitoringInterval);
    // Same rule for the allocation: only a changed value travels (the field is hub-gated server-side).
    const allocationChanged =
      newPromptAllocation !== undefined && newPromptAllocation !== currentProject.promptAllocation;
    const data: ProjectNameData = {
      projectId: id,
      projectName: currentProject.firmName,
      projectLabel: newProjectLabel.trim() || currentProject.projectLabel,
      projectCountry: newProjectCountry,
      ...(scheduleChanged ? { monitoringInterval: newMonitoringInterval } : {}),
      ...(allocationChanged ? { promptAllocation: newPromptAllocation } : {}),
    };

    onSaveStart?.(id);
    try {
      await changeProjectNameOrThrow(data);
      const patch = {
        projectLabel: data.projectLabel,
        localization: data.projectCountry,
        ...(scheduleChanged ? { monitoringInterval: newMonitoringInterval } : {}),
        ...(allocationChanged ? { promptAllocation: newPromptAllocation } : {}),
      };
      // The hub's tiles read the committed pool from the account; a changed reservation moves it.
      if (allocationChanged) {
        void refetchAccountSilently();
      }
      // Update project list context (ProjectsPage)
      setProjectResponse((prev) =>
        prev ? prev.map((p) => (p.id === id ? { ...p, ...patch } : p)) : prev
      );
      // Update single project query cache (ProjectsPreviewPage)
      queryClient.setQueryData<ProjectResponse>([GET_PROJECT, id], (prev) =>
        prev ? { ...prev, ...patch } : prev
      );
      // ProjectsPage reads rows from the server-paginated `projects-page-*` queries (neither
      // `projectResponse` nor `[GET_PROJECT, id]`). Patch every cached page IN PLACE rather than
      // invalidating — invalidation triggers a refetch (`isFetching`) and the table-wide loading
      // mask; `setQueriesData` updates the label instantly with no loader. No-op on V1/preview.
      const patchPage = (old: { items?: ProjectResponse[] } | undefined) =>
        old?.items
          ? { ...old, items: old.items.map((p) => (p.id === id ? { ...p, ...patch } : p)) }
          : old;
      queryClient.setQueriesData({ queryKey: ['projects-page-ungrouped'] }, patchPage);
      queryClient.setQueriesData({ queryKey: ['projects-page-group'] }, patchPage);
      // Keep the row spinner up a beat longer so a reveal animation has room to play.
      if (settleDelayMs > 0) {
        await new Promise((resolve) => setTimeout(resolve, settleDelayMs));
      }
    } catch (e) {
      console.error('Error changing name:', e);
      // The allocation rules answer with a typed 409 / 400 — say why instead of silently refreshing.
      const data = (e as { response?: { data?: { error?: string; missing?: number } } }).response?.data;
      if (data?.error === 'POOL_EXCEEDED' || data?.error === 'ALLOCATION_BELOW_USAGE') {
        confirmDialog({
          message:
            data.error === 'POOL_EXCEEDED'
              ? t('promptAllocationMissing', { count: data.missing ?? 0 })
              : t('promptAllocationLowerHint'),
          header: t('promptAllocation'),
          acceptLabel: 'OK',
          rejectClassName: 'hide',
        });
      }
      onRefresh();
    } finally {
      onSaveEnd?.(id);
    }
  };

  const editName = (id: number) => {
    if (!projectResponse) return;

    const currentProject = projectResponse.find((project) => project.id === id);
    if (!currentProject) return;

    newProjectLabel = currentProject.projectLabel;
    newProjectCountry = currentProject.localization;
    newMonitoringInterval = currentProject.monitoringInterval;
    newPromptAllocation = undefined;
    confirmDialog({
      message: (
        <ChangeName
          projectId={id}
          selectedProjectLabel={currentProject.projectLabel}
          selectedCountry={currentProject.localization}
          selectedMonitoringInterval={currentProject.monitoringInterval}
          onChangeProjectLabel={(label) => (newProjectLabel = label)}
          onChangeProjectCountry={(country) => (newProjectCountry = country)}
          onChangeMonitoringInterval={(interval) => (newMonitoringInterval = interval)}
          onChangeAllocation={(allocation) => (newPromptAllocation = allocation)}
          isSaveDisabled={updateSaveButtonState}
        />
      ),
      header: t('editProject'),
      defaultFocus: 'accept',
      rejectLabel: t('cancel'),
      acceptLabel: t('save'),
      acceptClassName: 'confirm-change-name-btn btn--disabled',
      accept: () => changeProjectNameEvent(id),
      onHide: resetValues,
    });
  };

  return { editName, resetValues };
};

export default useChangeName;
